import { faClose, faPlugCircleCheck, faRotate, faUnlink } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { ButtonIcon } from "../../../components/Button/Button"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { useGetCompany } from "../../../hooks/Company/useGetCompany"
import { formatPlatformFeePercent } from "../../../components/LandPage/Pricing"
import { usePayment } from "../../../hooks/Payment/usePayment"
import { useOrderPaymentProviders } from "../../../hooks/Payment/useOrderPaymentProviders"
import { EOrderPaymentProviderType } from "../../../Enum/EOrderPaymentProviderType"
import { companyMemory } from "../../../Objects/Memory/Memory"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"

interface Props {
  onClose: () => void
}

export const BillingPopup = ({ onClose }: Props) => {
  const { t } = useTranslation("frontend")

  const { getRestaurantOAuthLink, unlinkRestaurantOAuth } = usePayment()

  const { providers, handleGetProviders, handleUpdateProviderCapabilities } = useOrderPaymentProviders()

  const { setNewConfirmation } = useConfirmation()

  const { handleGetCompany } = useGetCompany()

  const [isConnecting, setIsConnecting] = useState(false)

  const [isUpdatingOnlinePayment, setIsUpdatingOnlinePayment] = useState(false)

  const [isUnlinking, setIsUnlinking] = useState(false)

  useEffect(() => {
    void handleGetProviders()
  }, [])

  const mercadoPagoProvider = providers.find((p) => p.providerType === EOrderPaymentProviderType.MercadoPago)

  const [isOnlinePaymentEnabled, setOnlinePaymentEnabled] = useState(false)

  const [isInPersonOrderPaymentEnabled, setInPersonOrderPaymentEnabled] = useState(false)

  const [isTapToPayEnabled, setTapToPayEnabled] = useState(false)

  useEffect(() => {
    setOnlinePaymentEnabled(mercadoPagoProvider?.isOnlinePaymentEnabled ?? false)

    setInPersonOrderPaymentEnabled(mercadoPagoProvider?.isInPersonPaymentEnabled ?? false)

    setTapToPayEnabled(mercadoPagoProvider?.isTapToPayEnabled ?? false)
  }, [mercadoPagoProvider])

  const paymentSettings = companyMemory.restaurantPaymentSettingsEntity

  const platformFeePercent = companyMemory.platformFeePercent ?? 0

  const isConnected = paymentSettings?.isConnectedApi ?? false

  const needsReconnect = paymentSettings?.needsReconnect ?? false

  const hasOAuthCredentials = paymentSettings?.hasOAuthCredentials ?? false

  const connectedAccount = paymentSettings?.mercadoPagoUserId

  const tokenExpiration = paymentSettings?.mercadoPagoTokenExpiresAt

  const tokenExpirationText = useMemo(() => {
    if (!tokenExpiration) {
      return "-"
    }

    return tokenExpiration.toLocaleDateTimeOrDash()
  }, [tokenExpiration])

  const connectionStatus = useMemo(() => {
    if (isConnected) {
      return {
        title: t("my_company.billing_connected"),
        description: t("my_company.billing_connected_description"),
        style: "bg-green-50 ring-2 ring-green-200 text-green-700",
      }
    }

    if (needsReconnect) {
      return {
        title: t("my_company.billing_reconnect_required"),
        description: t("my_company.billing_reconnect_required_description"),
        style: "bg-yellow-50 ring-2 ring-yellow-200 text-yellow-700",
      }
    }

    return {
      title: t("my_company.billing_not_connected"),
      description: t("my_company.billing_not_connected_description"),
      style: "bg-slate-50 ring-2 ring-slate-200 text-slate-700",
    }
  }, [isConnected, needsReconnect, t])

  const mercadoPagoDocsUrl = "https://www.mercadopago.com.br/blog/taxa-progressiva-point-mercado-pago"

  const formattedPlatformFee = useMemo(() => formatPlatformFeePercent(platformFeePercent), [platformFeePercent])

  const shouldShowConnectButton = !isConnected

  const canUnlinkOAuth = hasOAuthCredentials || isConnected

  const handleConnect = async () => {
    setIsConnecting(true)

    const response = await getRestaurantOAuthLink()

    setIsConnecting(false)

    if (response?.success && response.data?.authorizationUrl) {
      window.open(response.data.authorizationUrl, "_self")
    }
  }

  const syncStateFromMemory = () => {
    void handleGetCompany(() => {})
  }

  const handleUpdateCapabilities = async (next: {
    isOnlinePaymentEnabled: boolean
    isInPersonPaymentEnabled: boolean
    isTapToPayEnabled: boolean
  }) => {
    if (!isConnected || isUpdatingOnlinePayment) {
      return false
    }

    setIsUpdatingOnlinePayment(true)

    const isEnabled = next.isOnlinePaymentEnabled || next.isInPersonPaymentEnabled || next.isTapToPayEnabled

    const success = await handleUpdateProviderCapabilities({
      providerType: EOrderPaymentProviderType.MercadoPago,
      isEnabled,
      ...next,
    })

    setIsUpdatingOnlinePayment(false)

    return Boolean(success)
  }

  const handleToggleOnlinePayment = async (nextValue: boolean) => {
    const success = await handleUpdateCapabilities({
      isOnlinePaymentEnabled: nextValue,
      isInPersonPaymentEnabled: isInPersonOrderPaymentEnabled,
      isTapToPayEnabled: isTapToPayEnabled,
    })

    if (success) {
      setOnlinePaymentEnabled(nextValue)
    }
  }

  const handleToggleInPersonPayment = async (nextValue: boolean) => {
    const success = await handleUpdateCapabilities({
      isOnlinePaymentEnabled: isOnlinePaymentEnabled,
      isInPersonPaymentEnabled: nextValue,
      isTapToPayEnabled: isTapToPayEnabled,
    })

    if (success) {
      setInPersonOrderPaymentEnabled(nextValue)
    }
  }

  const handleUnlink = () => {
    if (isUnlinking) {
      return
    }

    setNewConfirmation(t("my_company.billing_unlink_title"), t("my_company.billing_unlink_description"), async () => {
      setIsUnlinking(true)

      await unlinkRestaurantOAuth(async () => {
        await handleGetCompany(syncStateFromMemory)

        await handleGetProviders()
      })

      setIsUnlinking(false)
    })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("my_company.billing_title")}
              icon={faPlugCircleCheck}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey="pages.myrestaurant.mycompany.billingpopup.1"
                    variant="global"
                    title={t("helpPopup:global.mercadoPago.title")}
                    description={t("helpPopup:global.mercadoPago.description")}
                    bullets={t("helpPopup:global.mercadoPago.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.billingPopup.button.onClose"
                  />
                </div>
              }
            />

            <Animate type={AnimationTypes.slideUpIn} duration="1.2s">
              <div className="mt-2 p-2 space-y-3">
                <section className={`rounded-lg p-4 ${connectionStatus.style}`}>
                  <p className="text-sm font-bold">{connectionStatus.title}</p>

                  <p className="text-xs mt-1">{connectionStatus.description}</p>
                </section>

                <section className="rounded-lg border-2 border-slate-200 bg-white p-4 space-y-2">
                  <div className="flex items-center justify-between text-sm text-slate-700">
                    <span className="font-semibold">{t("my_company.billing_connection_status")}</span>

                    <span className="font-bold">
                      {isConnected ? t("my_company.billing_connected") : t("my_company.billing_not_connected")}
                    </span>
                  </div>

                  {connectedAccount && (
                    <div className="flex items-center justify-between text-sm text-slate-700">
                      <span className="font-semibold">{t("my_company.billing_connected_account")}</span>

                      <span className="font-bold">{connectedAccount}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-sm text-slate-700">
                    <span className="font-semibold">{t("my_company.billing_token_expiration")}</span>

                    <span className="text-sm font-bold">{tokenExpirationText}</span>
                  </div>

                  {hasOAuthCredentials && !isConnected && (
                    <p className="text-xs text-yellow-700">{t("my_company.billing_reconnect_hint")}</p>
                  )}
                </section>

                <section className="rounded-lg border-2 border-orange-200 bg-orange-50 p-4 space-y-2 text-slate-700">
                  <p className="text-sm font-bold text-slate-800">{t("my_company.billing_oauth_info_title")}</p>

                  <p className="text-xs leading-5">{t("my_company.billing_oauth_info_description")}</p>

                  <p className="text-xs leading-5">
                    {platformFeePercent > 0
                      ? t("my_company.billing_oauth_fee_notice", { fee: formattedPlatformFee })
                      : t("my_company.billing_oauth_fee_zero")}
                  </p>

                  <a
                    href={mercadoPagoDocsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center text-xs font-semibold text-orange-700 underline underline-offset-2"
                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.myCompany.billingPopup.link.billingOauthDocsCta"
                  >
                    {t("my_company.billing_oauth_docs_cta")}
                  </a>
                </section>

                {isConnected ? (
                  <>
                    <SimpleToggle
                      title={t("my_company.enable_online_payment")}
                      value={isOnlinePaymentEnabled}
                      disabled={isUpdatingOnlinePayment}
                      onChange={handleToggleOnlinePayment}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.billingPopup.toggle.enableOnlinePayment"
                    />

                    <SimpleToggle
                      title={t("my_company.enable_in_person_order_payment")}
                      value={isInPersonOrderPaymentEnabled}
                      disabled={isUpdatingOnlinePayment}
                      onChange={handleToggleInPersonPayment}
                      descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.billingPopup.toggle.enableInPersonOrderPayment"
                    />
                  </>
                ) : (
                  <p className="text-xs text-slate-500">{t("my_company.billing_toggle_disabled_hint")}</p>
                )}

                {shouldShowConnectButton && (
                  <button
                    type="button"
                    disabled={isConnecting}
                    onClick={handleConnect}
                    className="w-full rounded-lg bg-orange-500 py-2 text-sm font-semibold text-white transition-colors hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.myCompany.billingPopup.button.handleConnect"
                  >
                    {needsReconnect ? (
                      <span className="inline-flex items-center gap-2">
                        <FontAwesomeIcon icon={faRotate} />
                        {t("my_company.billing_reconnect_button")}
                      </span>
                    ) : (
                      t("my_company.billing_connect_button")
                    )}
                  </button>
                )}

                {canUnlinkOAuth && (
                  <button
                    type="button"
                    disabled={isUnlinking}
                    onClick={handleUnlink}
                    className="w-full rounded-lg border border-red-200 bg-red-50 py-2 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.myCompany.billingPopup.button.handleUnlink"
                  >
                    <span className="inline-flex items-center gap-2">
                      <FontAwesomeIcon icon={faUnlink} />
                      {t("my_company.billing_unlink_button")}
                    </span>
                  </button>
                )}
              </div>
            </Animate>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
